/**
 * Organization Requests Service
 * Handles organization request operations
 */

import { supabase } from './supabase'

/**
 * Get all organization requests (Master Admin only)
 */
export const getAllOrganizationRequests = async (filters = {}) => {
  try {
    let query = supabase
      .from('organization_requests')
      .select('*')
      .order('created_at', { ascending: false })

    if (filters.status) {
      query = query.eq('status', filters.status)
    }

    const { data, error } = await query

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error fetching organization requests:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get user's own organization requests
 */
export const getUserOrganizationRequests = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    const { data, error } = await supabase
      .from('organization_requests')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error fetching user requests:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get single organization request
 */
export const getOrganizationRequest = async (requestId) => {
  try {
    const { data, error } = await supabase
      .from('organization_requests')
      .select('*')
      .eq('id', requestId)
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error fetching organization request:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Update organization request status (Master Admin only)
 */
export const updateOrganizationRequestStatus = async (requestId, updates) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Verify user is master admin
    const { data: masterAdminCheck } = await supabase
      .from('master_admins')
      .select('id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .maybeSingle()

    if (!masterAdminCheck) {
      throw new Error('Only master administrators can update request status')
    }

    const updateData = {
      ...updates,
      reviewed_by: user.id,
      reviewed_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    // If marking as contacted, update contact fields
    if (updates.status === 'contacted') {
      updateData.contacted_at = new Date().toISOString()
    }

    const { data, error } = await supabase
      .from('organization_requests')
      .update(updateData)
      .eq('id', requestId)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error updating organization request:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Approve organization request and create organization (Master Admin only)
 */
export const approveOrganizationRequest = async (requestId, organizationData = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Verify user is master admin
    const { data: masterAdminCheck } = await supabase
      .from('master_admins')
      .select('id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .maybeSingle()

    if (!masterAdminCheck) {
      throw new Error('Only master administrators can approve requests')
    }

    // Get request
    const { data: request, error: requestError } = await supabase
      .from('organization_requests')
      .select('*')
      .eq('id', requestId)
      .single()

    if (requestError) throw requestError
    if (!request) {
      throw new Error('Request not found')
    }

    // Create organization
    const orgSlug = `org-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    const { data: orgData, error: orgError } = await supabase.rpc('create_organization', {
      org_name: organizationData.name || request.requested_organization_name,
      org_slug: orgSlug,
      subscription_tier_param: organizationData.tier || 'demo',
    })

    if (orgError) throw orgError
    if (!orgData?.success) {
      throw new Error(orgData?.error || 'Failed to create organization')
    }

    // Ensure requestor is an org admin.
    // create_organization already adds the approving master admin as a member,
    // so when you approve your own request this must be idempotent.
    const { error: memberError } = await supabase
      .from('organization_members')
      .upsert(
        {
          organization_id: orgData.organization_id,
          user_id: request.user_id,
          role: 'admin',
        },
        { onConflict: 'organization_id,user_id' }
      )

    if (memberError) throw memberError

    // Update request status
    const { data: updatedRequest, error: updateError } = await supabase
      .from('organization_requests')
      .update({
        status: 'approved',
        reviewed_by: user.id,
        reviewed_at: new Date().toISOString(),
        created_organization_id: orgData.organization_id,
        updated_at: new Date().toISOString(),
      })
      .eq('id', requestId)
      .select()
      .single()

    if (updateError) throw updateError

    return {
      success: true,
      data: {
        request: updatedRequest,
        organization: {
          id: orgData.organization_id,
          name: organizationData.name || request.requested_organization_name,
        },
      },
    }
  } catch (error) {
    console.error('Error approving organization request:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Add contact notes to organization request (Master Admin only)
 */
export const addContactNotes = async (requestId, notes, contactMethod) => {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      throw new Error('User not authenticated')
    }

    // Verify user is master admin
    const { data: masterAdminCheck } = await supabase
      .from('master_admins')
      .select('id')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .maybeSingle()

    if (!masterAdminCheck) {
      throw new Error('Only master administrators can add contact notes')
    }

    const { data, error } = await supabase
      .from('organization_requests')
      .update({
        contact_notes: notes,
        contact_method_used: contactMethod,
        contacted_at: new Date().toISOString(),
        status: 'contacted',
        updated_at: new Date().toISOString(),
      })
      .eq('id', requestId)
      .select()
      .single()

    if (error) throw error
    return { success: true, data }
  } catch (error) {
    console.error('Error adding contact notes:', error)
    return { success: false, error: error.message }
  }
}

